import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { ENQ } from '@/constants/testIds';
import { CheckCircle2 } from 'lucide-react';

export function EnquiryDialog({ animal, open, onOpenChange, onSuccess, formIntro, successTemplate }) {
  const [form, setForm] = useState({ sponsor_name: '', membership_number: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.sponsor_name || !form.membership_number || !form.email || !form.phone) {
      toast.error('Please fill in all the required fields.');
      return;
    }
    if (!animal) return;
    setLoading(true);
    try {
      await api.post('/public/enquiries', { animal_id: animal.id, ...form });
      setSuccess(true);
      onSuccess?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (v) => {
    if (!v) {
      setTimeout(() => {
        setForm({ sponsor_name: '', membership_number: '', email: '', phone: '', message: '' });
        setSuccess(false);
      }, 200);
    }
    onOpenChange(v);
  };

  const successMessage = (successTemplate || 'Thank you. We are holding {animal_name} for you, and the Stable Office will be in touch within two working days. They are lucky to have you.').replace('{animal_name}', animal?.name || '');

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg rounded-xl bg-white border-border">
        {!success ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-extrabold">Sponsor {animal?.name}</DialogTitle>
            </DialogHeader>
            <p className="text-[15px] text-[#3E4A44] leading-relaxed">
              {formIntro || 'Tell us who you are and which horse or pony has found a place in your heart. The Stable Office will hold them for you and be in touch to arrange everything.'}
            </p>
            <form onSubmit={submit} data-testid={ENQ.form} className="space-y-4 pt-2">
              <div className="grid gap-1.5">
                <Label htmlFor="sponsor_name" className="font-semibold">Your name</Label>
                <Input id="sponsor_name" data-testid={ENQ.fieldName} value={form.sponsor_name} onChange={(e) => update('sponsor_name', e.target.value)} placeholder="Full name" required className="h-12 text-base" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="grid gap-1.5">
                  <Label htmlFor="membership_number" className="font-semibold">Club membership number</Label>
                  <Input id="membership_number" data-testid={ENQ.fieldMembership} inputMode="numeric" value={form.membership_number} onChange={(e) => update('membership_number', e.target.value)} required className="h-12 text-base" />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="phone" className="font-semibold">Phone</Label>
                  <Input id="phone" data-testid={ENQ.fieldPhone} inputMode="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} required className="h-12 text-base" />
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="email" className="font-semibold">Email</Label>
                <Input id="email" data-testid={ENQ.fieldEmail} type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required className="h-12 text-base" />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="message" className="font-semibold">A note for the Stable Office (optional)</Label>
                <Textarea id="message" data-testid={ENQ.fieldMessage} rows={3} value={form.message} onChange={(e) => update('message', e.target.value)} placeholder="Anything you'd like us to know…" className="text-base" />
              </div>
              <DialogFooter className="pt-2 gap-2">
                <Button type="button" variant="outline" onClick={() => handleClose(false)} className="btn rounded-lg h-12 font-bold">Cancel</Button>
                <Button type="submit" data-testid={ENQ.submitButton} disabled={loading} className="btn rounded-lg h-12 bg-[#1E3A2F] hover:bg-[#16302A] font-bold">
                  {loading ? 'Sending…' : 'Send enquiry'}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <div data-testid={ENQ.successState} className="py-6 text-center space-y-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#DEEFE4] flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-[#1E3A2F]" />
            </div>
            <div>
              <h3 className="text-2xl font-extrabold">Thank you</h3>
              <p className="text-[15px] text-[#3E4A44] mt-2 max-w-md mx-auto leading-relaxed">
                {successMessage}
              </p>
            </div>
            <Button data-testid={ENQ.successClose} onClick={() => handleClose(false)} className="btn rounded-lg h-12 bg-[#1E3A2F] hover:bg-[#16302A] font-bold">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
